import React, { useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import { useToast } from './ui/ToastProvider';
interface PendingCancellation {
  id: string;
  transactionId: string;
  mtoName: string;
  payoutType: 'FT' | 'IBFT' | 'OTC';
  amountFCY: string;
  currencyFCY: string;
  currentState: string;
  cancellationReason: string;
  cancellationAction: string;
  requestedBy: string;
  requestedAt: string;
  status: 'PENDING';
}
// Mock data
const MOCK_PENDING_CANCELLATIONS: PendingCancellation[] = Array(10).fill(null).map((_, index) => ({
  id: `CANC-${40000 + index}`,
  transactionId: `TXN-${30000 + index}`,
  mtoName: ['RIA Money Transfer', 'InstantCash', 'MoneyGram'][index % 3],
  payoutType: ['FT', 'IBFT', 'OTC'][index % 3] as 'FT' | 'IBFT' | 'OTC',
  amountFCY: (1000 + index * 100).toFixed(2),
  currencyFCY: 'USD',
  currentState: ['DOWNLOAD', 'SCREENING', 'TITLE_FETCH'][index % 3],
  cancellationReason: ['Duplicate transaction', 'Customer request', 'Incorrect beneficiary details'][index % 3],
  cancellationAction: 'SIMPLE_CANCEL',
  requestedBy: ['Admin User', 'John Doe', 'Jane Smith'][index % 3],
  requestedAt: new Date(Date.now() - index * 3600000).toISOString(),
  status: 'PENDING'
}));
const CheckerCancellationTable: React.FC = () => {
  const [cancellations, setCancellations] = useState<PendingCancellation[]>(MOCK_PENDING_CANCELLATIONS);
  const [selectedCancellation, setSelectedCancellation] = useState<PendingCancellation | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  const handleAction = (cancellation: PendingCancellation, action: 'approve' | 'reject') => {
    setSelectedCancellation(cancellation);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selectedCancellation || !actionType) return;
    if (!reason.trim()) {
      return;
    }
    setIsLoading(true);
    try {
      // Remove from pending list
      setCancellations(prev => prev.filter(c => c.id !== selectedCancellation.id));
      showToast(`Cancellation request ${actionType === 'approve' ? 'approved' : 'rejected'} for ${selectedCancellation.transactionId}`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelectedCancellation(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewDetails = (cancellation: PendingCancellation) => {
    setSelectedCancellation(cancellation);
    setDetailsDrawerOpen(true);
  };
  const rowActions: RowAction<PendingCancellation>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewDetails(row)
  }, {
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<PendingCancellation>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-543" data-mp="2xtlrotea4o44">
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-544" data-mp="2tx7mqwqt8vm1">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-545" data-mp="2yzgi1zr5o9rb">
          {row.mtoName}
        </Typography>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: 'rgba(139, 92, 246, 0.1)',
      color: '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-546" data-mp="26arc9nf5ng98" />
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-547" data-mp="1niyjnu3zzx21">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'currentState',
    label: 'Current State',
    renderCell: row => <Chip label={row.currentState.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-548" data-mp="1i6tgz5a9ry8q" />
  }, {
    id: 'cancellationReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 200
    }} data-id="element-549" data-mp="2155qwt909c5d">
          {row.cancellationReason}
        </Typography>
  }, {
    id: 'cancellationAction',
    label: 'Action Type',
    renderCell: row => <Typography variant="body2" fontSize="0.75rem" noWrap data-id="element-550" data-mp="areyfm55thko">
          {row.cancellationAction.replace(/_/g, ' ')}
        </Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-551" data-mp="2ifrg2fq1cak3">
          {row.requestedBy}
        </Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-552" data-mp="23pbugztpj0le">
          {new Date(row.requestedAt).toLocaleString()}
        </Typography>
  }];
  return <>
      <DataTable title="Pending Cancellation Requests" data={cancellations} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending cancellation requests" initialVisibleColumns={['id', 'transactionId', 'mtoName', 'payoutType', 'amountFCY', 'currentState', 'cancellationReason', 'cancellationAction', 'requestedBy', 'requestedAt']} onRowClick={handleViewDetails} data-id="element-553" data-mp="1uqgp1pyhluj5" />

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-554" data-mp="3rkqekdir8ant">
        <DialogTitle data-id="element-555" data-mp="5671ql9fh3jg">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-556" data-mp="56gtada4fir9">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-557" data-mp="38nfg2v4y5l9f" />
            <Typography variant="h6" fontWeight={600} data-id="element-558" data-mp="swt724s1s9pr">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Cancellation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-559" data-mp="1izy9bhdd8gwm">
          <Box sx={{
          mt: 1
        }} data-id="element-560" data-mp="1izeyslq1ousm">
            {selectedCancellation && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 1
            }} data-id="element-561" data-mp="1e2oo6dx0erel">
                  Transaction ID:{' '}
                  <strong data-id="element-562" data-mp="21qfy5av2uscp">{selectedCancellation.transactionId}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-563" data-mp="1xoupy2thn28u">
                  Maker's Reason:{' '}
                  <strong data-id="element-564" data-mp="26qkhd8nde16m">{selectedCancellation.cancellationReason}</strong>
                </Typography>
              </>}

            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this cancellation...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-565" data-mp="hauscyi47oor" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-566" data-mp="2j620fzrb10vg">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-567" data-mp="25agfc3o6z18b">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-569" data-mp="3unbm8sxwo4zx" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-570" data-mp="3ukxqehveiorb" />} data-id="element-568" data-mp="1vb935jt71cms">
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Drawer */}
      {selectedCancellation && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedCancellation(null);
    }} transaction={{
      id: selectedCancellation.transactionId,
      mtoName: selectedCancellation.mtoName,
      mtoRef: `MTO-${selectedCancellation.transactionId}`,
      bankRef: `BNK-${selectedCancellation.transactionId}`,
      branchCode: '',
      agentCode: '',
      payoutType: selectedCancellation.payoutType,
      amountFCY: selectedCancellation.amountFCY,
      currencyFCY: selectedCancellation.currencyFCY,
      amountPKR: (parseFloat(selectedCancellation.amountFCY) * 280).toString(),
      corridor: 'PK-US',
      purposeCode: 'FAM',
      purposeText: 'Family Support',
      currentState: selectedCancellation.currentState as any,
      lifecycleStatus: 'IN_PROGRESS' as any,
      createdAt: selectedCancellation.requestedAt,
      updatedAt: selectedCancellation.requestedAt,
      remitterName: 'Remitter Name',
      beneficiaryName: 'Beneficiary Name',
      beneficiaryIBAN: 'PK00000000000000000000',
      railRefs: {},
      stateHistory: []
    } as any} data-id="element-571" data-mp="1ekctw7k9krw5" />}
    </>;
};
export default CheckerCancellationTable;